import { GlobeIcon, ShieldIcon } from 'lucide-react'

import type { NetworkPool } from '../types/network'
import { cn } from '../utils'
import { Badge } from './badge'

type NetworkTypeBadgeProps = {
  pool: NetworkPool
  showIsolationStatus?: boolean
  className?: string
}

/**
 * NetworkTypeBadge - Visual indicator for network pool type and isolation status
 *
 * Shows:
 * - Network type (Bridge or SDN VNET)
 * - Isolation status (Shared = global conflicts, Isolated = per-org conflicts)
 *
 * Color coding:
 * - Shared Bridge: Blue (global infrastructure)
 * - Isolated VNET: Purple (organization-specific)
 */
export function NetworkTypeBadge({
  pool,
  showIsolationStatus = true,
  className,
}: NetworkTypeBadgeProps) {
  const isShared = pool.is_shared || pool.network_type === 'bridge'
  const networkTypeLabel = pool.network_type === 'bridge' ? 'Bridge' : 'SDN VNET'

  // Color schemes
  const colorClasses = isShared
    ? 'bg-blue-100 text-blue-700 border-blue-200'
    : 'bg-purple-100 text-purple-700 border-purple-200'

  const Icon = isShared ? GlobeIcon : ShieldIcon

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Network Type Badge */}
      <Badge className={cn(colorClasses, 'flex items-center gap-1.5')}>
        <Icon className="h-3 w-3" />
        <span>{networkTypeLabel}</span>
      </Badge>

      {/* Isolation Status (optional) */}
      {showIsolationStatus && (
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            isShared
              ? 'border-blue-300 text-blue-600 bg-blue-50'
              : 'border-purple-300 text-purple-600 bg-purple-50',
          )}
        >
          {isShared ? 'Shared (Global Conflicts)' : 'Isolated (Per-Org Conflicts)'}
        </Badge>
      )}
    </div>
  )
}

/**
 * NetworkIsolationIndicator - Simplified version showing only isolation status
 */
export function NetworkIsolationIndicator({
  isShared,
  className,
}: {
  isShared: boolean
  className?: string
}) {
  const Icon = isShared ? GlobeIcon : ShieldIcon
  const label = isShared ? 'Shared' : 'Isolated'
  const colorClasses = isShared
    ? 'bg-blue-100 text-blue-700 border-blue-200'
    : 'bg-purple-100 text-purple-700 border-purple-200'

  return (
    <Badge className={cn(colorClasses, 'flex items-center gap-1.5', className)}>
      <Icon className="h-3 w-3" />
      <span>{label}</span>
    </Badge>
  )
}
